/**
 * Communications Center service, backed by the API.
 *
 * Deliberately thin: every call it makes already exists as a domain service, so
 * this composes them into the one boundary the modal talks to rather than
 * opening another `HttpClient`. The only work it does of its own is narrowing
 * the untyped client-search payload and assembling the dropdown lists.
 */

import type { ICommunicationService } from "../communication/ICommunicationService";
import type { ICommunicationManagementService } from "../communicationManagement/ICommunicationManagementService";
import type { IRefCommunicationTemplatesService } from "../refCommunicationTemplates/IRefCommunicationTemplatesService";
import type { IRefOptionsService } from "../refOptions/IRefOptionsService";
import type { IRefServicesService } from "../refServices/IRefServicesService";
import type { IRefStoresService } from "../refStores/IRefStoresService";
import {
  CommunicationFilterOption,
  CommunicationFilterOptions,
  CommunicationSearchRequestOptions,
  CommunicationSend,
  CommunicationSendDelivery,
  CommunicationSendPageResponse,
  DeliveryFilters,
  DeliveryPageResponse,
  RecipientPageResponse,
  RecipientSearchParameters,
  ResponsePageResponse,
  SendCreateData,
  SendSearchParameters,
  TemplateListParameters,
  TemplatePageResponse,
  TemplateWriteData,
} from "@/types/communicationsCenter";
import { ICommunicationsCenterService } from "./ICommunicationsCenterService";
import { adaptRecipients } from "./adapters";

const byName = (a: CommunicationFilterOption, b: CommunicationFilterOption) =>
  a.name.localeCompare(b.name, "pt");

/**
 * The domain services this one is built from.
 *
 * Injected rather than imported from their barrels: those barrels resolve
 * through `ServiceFactory`, and `ServiceFactory` constructs this class, so
 * importing them directly forms a cycle that leaves the factory undefined at
 * module-evaluation time.
 */
export interface CommunicationsCenterDependencies {
  communicationService: ICommunicationService;
  communicationManagementService: ICommunicationManagementService;
  refCommunicationTemplatesService: IRefCommunicationTemplatesService;
  refServicesService: IRefServicesService;
  refOptionsService: IRefOptionsService;
  refStoresService: IRefStoresService;
}

export class CommunicationsCenterService implements ICommunicationsCenterService {
  private readonly communicationService: ICommunicationService;
  private readonly communicationManagementService: ICommunicationManagementService;
  private readonly refCommunicationTemplatesService: IRefCommunicationTemplatesService;
  private readonly refServicesService: IRefServicesService;
  private readonly refOptionsService: IRefOptionsService;
  private readonly refStoresService: IRefStoresService;

  constructor(dependencies: CommunicationsCenterDependencies) {
    this.communicationService = dependencies.communicationService;
    this.communicationManagementService =
      dependencies.communicationManagementService;
    this.refCommunicationTemplatesService =
      dependencies.refCommunicationTemplatesService;
    this.refServicesService = dependencies.refServicesService;
    this.refOptionsService = dependencies.refOptionsService;
    this.refStoresService = dependencies.refStoresService;
  }

  async searchSends(
    parameters: SendSearchParameters,
    options: CommunicationSearchRequestOptions = {},
  ): Promise<CommunicationSendPageResponse> {
    return this.communicationService.search(parameters, options);
  }

  async searchDeliveries(
    communicationId: number,
    parameters: DeliveryFilters = {},
    options: CommunicationSearchRequestOptions = {},
  ): Promise<DeliveryPageResponse> {
    return this.communicationService.searchDeliveries(
      communicationId,
      parameters,
      options,
    );
  }

  async searchResponses(
    communicationId: number,
    options: CommunicationSearchRequestOptions = {},
  ): Promise<ResponsePageResponse> {
    return this.communicationService.searchResponses(communicationId, options);
  }

  async getDeliveryStatusCodes(): Promise<string[]> {
    return this.communicationService.getDeliveryStatusCodes();
  }

  async getStatusCodes(): Promise<string[]> {
    return this.communicationService.getStatusCodes();
  }

  async listTemplates(
    parameters: TemplateListParameters = {},
  ): Promise<TemplatePageResponse> {
    const { search, ...rest } = parameters;

    const response = await this.refCommunicationTemplatesService.getAll({
      ...rest,
      /*
       * External templates are owned by another system: the backoffice neither
       * lists them nor offers them for a send. Defaulted rather than forced, so
       * a caller that genuinely wants them can still ask.
       */
      isExternal: parameters.isExternal ?? false,
      /*
       * Follow-up models belong to the appointment flow — `FollowUpModal`
       * offers them and this backoffice neither lists nor sends them, which is
       * the same rule its writes follow. `GET /api/refCommunicationTemplates`
       * takes the filter, so it is applied by the API rather than here.
       */
      isFollowUp: parameters.isFollowUp ?? false,
      size: parameters.size ?? 100,
    });

    if (!search?.trim()) return response;

    /*
     * The endpoint has no text search, so name/subject matching is done here.
     * It only filters the page already fetched, which is why the caller asks
     * for a large page rather than paging server-side.
     */
    const needle = search.trim().toLowerCase();
    const content = response.content.filter(template =>
      `${template.name} ${template.subject}`.toLowerCase().includes(needle),
    );

    return {
      content,
      page: { ...response.page, totalElements: content.length, totalPages: 1 },
    };
  }

  /**
   * Both template writes send `isFollowUp: false`, on create and on update.
   *
   * Follow-up models belong to the appointment flow and are not authored here,
   * so a model written through the backoffice is never one — and saying so
   * explicitly keeps the API from inferring the flag. `FollowUpModal` does not
   * write templates, so nothing it relies on passes through here.
   */
  async createTemplate(templateData: TemplateWriteData): Promise<number> {
    return this.refCommunicationTemplatesService.create({
      ...templateData,
      isFollowUp: false,
    });
  }

  async updateTemplate(
    id: number,
    templateData: TemplateWriteData,
  ): Promise<void> {
    return this.refCommunicationTemplatesService.update(id, {
      ...templateData,
      isFollowUp: false,
    });
  }

  async deleteTemplate(id: number): Promise<void> {
    return this.refCommunicationTemplatesService.delete(id);
  }

  async searchRecipients(
    parameters: RecipientSearchParameters,
    options: CommunicationSearchRequestOptions = {},
  ): Promise<RecipientPageResponse> {
    const response = await this.communicationService.searchClients(
      parameters,
      options,
    );

    return {
      content: adaptRecipients(response.content),
      page: response.page,
    };
  }

  /**
   * Send a communication written in the backoffice to its whole audience.
   *
   * A bulk send carries the audience filters, not a contact list: the API
   * resolves the clients itself, in the background, and answers at once with
   * the communication `INITIALIZING`. It has no `isFollowUp` — a bulk send is
   * never one, and the API refuses a follow-up template.
   */
  async createSend(sendData: SendCreateData): Promise<CommunicationSend> {
    return this.communicationManagementService.createBulk(sendData);
  }

  async resendSend(
    communicationId: number,
    includeSkipped = false,
  ): Promise<CommunicationSend> {
    return this.communicationManagementService.resend(
      communicationId,
      includeSkipped,
    );
  }

  async resendToClient(
    communicationId: number,
    clientId: number,
  ): Promise<CommunicationSendDelivery> {
    return this.communicationManagementService.resendToClient(
      communicationId,
      clientId,
    );
  }

  async cancelSend(communicationId: number): Promise<void> {
    return this.communicationManagementService.cancel(communicationId);
  }

  async cancelDelivery(deliveryId: number): Promise<void> {
    return this.communicationManagementService.cancelDelivery(deliveryId);
  }

  /**
   * The dropdown lists, gathered in parallel.
   *
   * `allSettled` on purpose: these are filters, not required input, so one list
   * failing leaves the others usable and that dropdown simply offers "all".
   * None of the reference services accepts an abort signal, so the caller's
   * signal is only checked once the work has settled.
   */
  async getFilterOptions(
    signal?: AbortSignal,
  ): Promise<CommunicationFilterOptions> {
    const [templates, services, serviceTypes, species, stores] =
      await Promise.allSettled([
        /* Internal, non-follow-up templates only — see `listTemplates`. The
           Modelo dropdowns have to offer exactly what the Modelos tab lists. */
        this.refCommunicationTemplatesService.getAll({
          size: 100,
          isExternal: false,
          isFollowUp: false,
        }),
        this.refServicesService.getAll(),
        this.refOptionsService.getOptionsByCode("SERVICE_TYPE"),
        this.refOptionsService.getOptionsByCode("SPECIES"),
        this.refStoresService.getAllHubs(),
      ]);

    if (signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }

    return {
      templates:
        templates.status === "fulfilled" ? (templates.value.content ?? []) : [],
      services:
        services.status === "fulfilled"
          ? (services.value.content ?? [])
              .map(service => ({ id: service.id, name: service.name }))
              .sort(byName)
          : [],
      serviceTypes:
        serviceTypes.status === "fulfilled"
          ? serviceTypes.value
              .map(option => ({ id: option.id, name: option.value }))
              .sort(byName)
          : [],
      species:
        species.status === "fulfilled"
          ? species.value
              .map(option => ({ id: option.id, name: option.value }))
              .sort(byName)
          : [],
      stores:
        stores.status === "fulfilled"
          ? (stores.value.content ?? [])
              .map(store => ({ id: store.id, name: store.name }))
              .sort(byName)
          : [],
    };
  }
}
